'use client';

import { FC } from 'react';

import classNames from 'classnames';

import { Badge, LuvImage } from '@/components/ui';

import { CLICKED_ITEM_TYPES, trackItemClickedEvent } from '@/utils/rudderstack';

import { ItemSelectorProps, TypeItem } from '../../ItemSelector';

import EditChild from '../ChildCompatible/EditChild';

export type ItemProps = {
  // Common props
  item: TypeItem;
  isSelected: boolean;
  setSelectedItemId: ItemSelectorProps['setSelectedItemId'];
  trackCtaName: ItemSelectorProps['trackCtaName'];
  trackPageLocation: ItemSelectorProps['trackPageLocation'];
  // Child specific props
  hasEditChild: ItemSelectorProps['hasEditChild'];
  editChildText: string;
};

const TITLE_CHARACTER_COUNT = 16;

const _formatTitle = (title: string) =>
  title.length > TITLE_CHARACTER_COUNT
    ? `${title.slice(0, TITLE_CHARACTER_COUNT)}...`
    : title;

const Item: FC<ItemProps> = ({
  // Common props
  item,
  isSelected,
  setSelectedItemId,
  trackCtaName,
  trackPageLocation,
  // Child specific props
  hasEditChild,
  editChildText,
}) => {
  const handleClick = () => {
    // NoOp if already selected
    if (isSelected) return;

    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.LINK,
      pageLocation: trackPageLocation,
      cta: trackCtaName,
    });

    setSelectedItemId(item.id);
  };

  const { badgeDetails } = item;

  return (
    <div data-testid="item" className="flex flex-col content-center">
      <button
        className={classNames({
          'cursor-default disabled': isSelected,
          'relative flex flex-col items-center': badgeDetails,
        })}
        onClick={() => handleClick()}
      >
        <div
          className={classNames(
            'relative w-[88px] h-[88px] lg:w-28 lg:h-28 mx-auto flex items-center overflow-hidden z-10 rounded-full',
            {
              'bg-indigo-pastel border-[2px] border-blue-deep': isSelected,
              'border-[1px] hover:border-[2px] border-grey-dark hover:border-indigo-extra-dark':
                !isSelected,
            },
          )}
        >
          <LuvImage imageData={item.icon} className="w-11 lg:w-14 mx-auto" />
        </div>

        {badgeDetails && (
          <div className="absolute z-10 bottom-6 md:bottom-7">
            <Badge
              badgeColor={badgeDetails.color}
              text={badgeDetails.text}
              classes="font-normal"
            />
          </div>
        )}

        <p className="font-bold text-center text-base lg:text-xl">
          {_formatTitle(item.title)}
        </p>

        {hasEditChild && isSelected && (
          <EditChild
            id={item.id}
            cta={editChildText}
            trackPageLocation={trackPageLocation}
          />
        )}
      </button>
    </div>
  );
};

export default Item;
